import { type NavigateOptions, useNavigate } from "react-router-dom";

import { useUserStore } from "@/stores/use-user-store";
import { buildDepartmentRoute } from "@/utils/department-routes";
import { getFirstUserDepartment } from "@/utils/user-positions";

import { useDepartmentRoute } from "./use-department-route";

/**
 * Hook para navegação programática com prefixo de department.
 *
 * Este hook retorna uma função de navegação que automaticamente adiciona
 * o prefixo de department da URL às rotas. Se a URL não tiver um department
 * válido, usa o department do usuário autenticado como fallback.
 *
 * @returns Função de navegação que aceita um path e navega para /{department-slug}/{path}
 *
 * @example
 * // Para usuário em /casting/talentos
 * const navigate = useDepartmentNavigate();
 * navigate('/talentos'); // navega para /casting/talentos
 * navigate('jobs/novo'); // navega para /casting/jobs/novo
 *
 * @example
 * // Uso em componente
 * function MyComponent() {
 *   const navigate = useDepartmentNavigate();
 *
 *   const handleClick = () => {
 *     navigate('/talentos/123');
 *   };
 *
 *   return <button onClick={handleClick}>Ver Talento</button>;
 * }
 *
 * ⚠️ **Fallback**: Se o usuário não estiver autenticado (activeUser === null),
 * a navegação será feita sem prefixo de department.
 */
export function useDepartmentNavigate({
  shouldGetDepartmentFromUrl = true,
}: {
  shouldGetDepartmentFromUrl?: boolean;
} = {}) {
  const navigate = useNavigate();
  const activeUser = useUserStore((state) => state.activeUser);
  const { departmentSlug } = useDepartmentRoute();

  return (path: string, options?: NavigateOptions) => {
    if (!activeUser) {
      navigate(path, options);
      return;
    }

    const department =
      shouldGetDepartmentFromUrl && departmentSlug ? departmentSlug : getFirstUserDepartment(activeUser);

    if (!department) {
      navigate(path, options);
      return;
    }

    const route = buildDepartmentRoute(department, path);
    navigate(route, options);
  };
}
